import { useState, useEffect, useCallback } from "react";
import {
  getAdminAdvertising,
  createAdminAdvertising,
  updateAdminAdvertising,
  updateAdminAdvertisingStatus,
} from "../../lib/api";
import { toList } from "../../lib/apiAdminUtils";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, MoreHorizontal } from "lucide-react";
import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { ALLOWED_AD_PLACEMENTS, FORBIDDEN_PDF_OR_REPORT_PLACEMENTS } from "../../constants/adPlacements";

const STATUSES = ["prospect", "negotiating", "active", "expired", "paused"];
const STATUS_LABELS = {
  prospect: "Prospecto",
  negotiating: "Negociando",
  active: "Activo",
  expired: "Expirado",
  paused: "Pausado",
};
const STATUS_COLORS = {
  prospect: "bg-blue-100 text-blue-800 border-blue-200",
  negotiating: "bg-amber-100 text-amber-800 border-amber-200",
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  expired: "bg-slate-100 text-slate-800 border-slate-200",
  paused: "bg-slate-100 text-slate-800 border-slate-200",
};

function formatDate(s) {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return s;
  }
}

const emptyAd = {
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  product_type: "",
  banner_image_url: "",
  target_url: "",
  start_date: "",
  end_date: "",
  agreed_amount: "",
  status: "prospect",
  notes: "",
};

export default function AdminAdvertising() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyAd);

  const fetchAds = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter && statusFilter !== "all" ? { status: statusFilter } : {};
      const res = await getAdminAdvertising(params);
      setItems(toList(res?.data));
    } catch (err) {
      setItems([]);
      toast.error("Error al cargar publicidad");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchAds();
  }, [fetchAds]);

  const openCreate = () => {
    setForm(emptyAd);
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEdit = (ad) => {
    setForm({
      company_name: ad.company_name || "",
      contact_name: ad.contact_name || "",
      email: ad.email || "",
      phone: ad.phone || "",
      product_type: ad.product_type || "",
      banner_image_url: ad.banner_image_url || "",
      target_url: ad.target_url || "",
      start_date: ad.start_date ? ad.start_date.slice(0, 10) : "",
      end_date: ad.end_date ? ad.end_date.slice(0, 10) : "",
      agreed_amount: ad.agreed_amount != null ? String(ad.agreed_amount) : "",
      status: ad.status || "prospect",
      notes: ad.notes || "",
    });
    setEditingId(ad.id);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (payload.start_date === "") delete payload.start_date;
      if (payload.end_date === "") delete payload.end_date;
      if (payload.agreed_amount === "") delete payload.agreed_amount;
      else payload.agreed_amount = parseFloat(payload.agreed_amount) || null;
      if (editingId) {
        await updateAdminAdvertising(editingId, payload);
        toast.success("Anunciante actualizado");
      } else {
        await createAdminAdvertising(payload);
        toast.success("Anunciante creado");
      }
      setDialogOpen(false);
      fetchAds();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateAdminAdvertisingStatus(id, status);
      toast.success("Estado actualizado");
      fetchAds();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-sky-100 bg-sky-50/40">
        <CardContent className="pt-6 pb-6 text-sm text-slate-700 space-y-2">
          <p className="font-semibold text-slate-800">Ubicaciones permitidas (solo app web)</p>
          <ul className="list-disc list-inside space-y-1 text-slate-600">
            {ALLOWED_AD_PLACEMENTS.map((p) => (
              <li key={p.value}>
                <code className="text-xs bg-white/80 px-1 rounded">{p.value}</code> — {p.label}
              </li>
            ))}
          </ul>
          <p className="text-slate-600 pt-2 border-t border-sky-100">
            <strong className="text-slate-800">Sin publicidad en PDFs:</strong> informes clínicos,
            guías familiares, recetas y documentos descargables no muestran anuncios. No usar
            valores como {FORBIDDEN_PDF_OR_REPORT_PLACEMENTS.join(", ")}.
          </p>
          <p className="text-slate-600">
            En banners digitales, el enlace del anunciante debe abrir en nueva pestaña{" "}
            <code className="text-xs bg-white/80 px-1 rounded">target=&quot;_blank&quot;</code>{" "}
            con <code className="text-xs bg-white/80 px-1 rounded">rel=&quot;noopener noreferrer&quot;</code>.
          </p>
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Publicidad / Sponsors</h1>
          <p className="text-slate-500 mt-1">Campañas para la landing pública · <Link to="/contacto" className="text-sky-600 hover:underline">Contacto comercial</Link></p>
        </div>
        <Button onClick={openCreate} className="bg-slate-800 hover:bg-slate-700">
          <Plus className="h-4 w-4 mr-2" />
          Nuevo anunciante
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center gap-4">
            <CardTitle className="text-lg">Lista de anunciantes</CardTitle>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-slate-500">Cargando...</div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-slate-500">No hay anunciantes</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Contacto</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Producto</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Inicio</TableHead>
                  <TableHead>Fin</TableHead>
                  <TableHead className="w-[60px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((ad) => (
                  <TableRow key={ad.id}>
                    <TableCell className="font-medium">{ad.company_name || "-"}</TableCell>
                    <TableCell>{ad.contact_name || "-"}</TableCell>
                    <TableCell>{ad.email || "-"}</TableCell>
                    <TableCell>{ad.product_type || "-"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_COLORS[ad.status] || ""}>
                        {STATUS_LABELS[ad.status] || ad.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(ad.start_date)}</TableCell>
                    <TableCell>{formatDate(ad.end_date)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(ad)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          {STATUSES.filter((s) => s !== ad.status).map((s) => (
                            <DropdownMenuItem
                              key={s}
                              onClick={() => handleStatusChange(ad.id, s)}
                            >
                              Estado → {STATUS_LABELS[s]}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar anunciante" : "Nuevo anunciante"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Empresa</Label>
                <Input
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Nombre contacto</Label>
                <Input
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Tipo de producto</Label>
                <Input
                  value={form.product_type}
                  onChange={(e) => setForm({ ...form, product_type: e.target.value })}
                />
              </div>
              <div>
                <Label>Estado</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm({ ...form, status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {STATUS_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>URL del banner</Label>
                <Input
                  value={form.banner_image_url}
                  onChange={(e) => setForm({ ...form, banner_image_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div>
                <Label>URL destino</Label>
                <Input
                  value={form.target_url}
                  onChange={(e) => setForm({ ...form, target_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div>
                <Label>Fecha inicio</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Fecha fin</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Monto acordado</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.agreed_amount}
                  onChange={(e) => setForm({ ...form, agreed_amount: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-slate-800 hover:bg-slate-700">
                {editingId ? "Guardar" : "Crear"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
